// Access tokens — master admin screen.
//
// App-to-app authorization AGAINST this admin app: global bearer tokens that
// let an external application authenticate to the master-admin API
// (machine-to-machine), separate from operator login. The plaintext is shown
// ONCE at creation; only its SHA-256 hash is stored.
//
// These ARE enforced: RequireAccessToken gates the app-to-app surfaces —
// the partner API (/api/v1/partner/*) and the S3 access policy endpoints
// (/api/v1/s3-access/*). A token also owns the bypass rules / partner clients
// created through it (per-token ownership).

import { useEffect, useState } from 'react';
import { I } from '../components/icons';
import { Modal, useToast, Empty } from '../components/ui';
import {
  fetchAccessTokens, createAccessToken, revokeAccessToken,
  ApiError, type AccessToken, type CreateAccessTokenResponse,
} from '../api/client';

const fmtDate = (s?: string) => (s ? new Date(s).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' }) : '—');

const isDead = (t: AccessToken) => !!t.revokedAt || (!!t.expiresAt && new Date(t.expiresAt) < new Date());

// TokenRow renders one token row. onRevoke is omitted for dead (revoked/expired)
// tokens, which show no action.
function TokenRow({ t, onRevoke }: { t: AccessToken; onRevoke?: (t: AccessToken) => void }) {
  const revoked = !!t.revokedAt;
  const expired = !revoked && !!t.expiresAt && new Date(t.expiresAt) < new Date();
  const dead = revoked || expired;
  return (
    <tr style={{ opacity: dead ? 0.6 : 1 }}>
      <td><span className="mono" style={{ fontSize: 12 }}>{t.label}</span></td>
      <td className="muted" style={{ fontSize: 11.5 }}>{fmtDate(t.createdAt)}</td>
      <td className="muted" style={{ fontSize: 11.5 }}>{t.expiresAt ? fmtDate(t.expiresAt) : 'never'}</td>
      <td className="muted" style={{ fontSize: 11.5 }}>{t.lastUsedAt ? fmtDate(t.lastUsedAt) : '—'}</td>
      <td>
        {revoked ? <span className="badge" style={{ background: 'var(--red-bg)', color: 'var(--red)' }}>revoked</span>
         : expired ? <span className="badge" style={{ background: 'var(--amber-bg)', color: 'var(--amber)' }}>expired</span>
         : <span className="badge" style={{ background: 'var(--accent-soft)', color: 'var(--accent)' }}>active</span>}
      </td>
      <td>
        {onRevoke && !dead && (
          <button className="btn btn-ghost btn-icon btn-sm" title="Revoke" aria-label="Revoke token"
                  onClick={() => onRevoke(t)}>
            <I.Trash size={12}/>
          </button>
        )}
      </td>
    </tr>
  );
}

const TOKEN_COLS = (
  <tr>
    <th>Label</th>
    <th style={{ width: 140 }}>Created</th>
    <th style={{ width: 140 }}>Expires</th>
    <th style={{ width: 140 }}>Last used</th>
    <th style={{ width: 100 }}>Status</th>
    <th style={{ width: 64 }}></th>
  </tr>
);

// AccessTokensScreen lists + manages the app-to-app access tokens. Bypass rules
// (which can be owned by a token) live on their own page — see BypassRules.tsx.
export function AccessTokensScreen() {
  const toast = useToast();
  const [tokens, setTokens] = useState<AccessToken[]>([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);

  const [addOpen, setAddOpen] = useState(false);
  const [newPlain, setNewPlain] = useState<CreateAccessTokenResponse | null>(null);
  const [revokeTarget, setRevokeTarget] = useState<AccessToken | null>(null);
  const [revoking, setRevoking] = useState(false);

  const reload = async () => {
    setLoading(true);
    setErr(null);
    try {
      setTokens(await fetchAccessTokens());
    } catch (e) {
      setErr(e instanceof ApiError ? e.message : String(e));
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { reload(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, []);

  const doRevoke = async () => {
    const t = revokeTarget;
    if (!t || revoking) return;
    setRevoking(true);
    try {
      await revokeAccessToken(t.id);
      await reload();
      setRevokeTarget(null);
      toast?.success('Token revoked', t.label);
    } catch (e) {
      toast?.error('Revoke failed', e instanceof ApiError ? e.message : String(e));
    } finally {
      setRevoking(false);
    }
  };

  const active = tokens.filter((t) => !isDead(t));
  const dead = tokens.filter(isDead);

  return (
    <div className="page-narrow">
      <div className="page-head spread">
        <div>
          <h1 className="page-title">Access tokens</h1>
          <p className="page-sub">
            Bearer tokens for external apps to call this API machine-to-machine (separate from operator login).
            A token authenticates the <strong>Partner API</strong> (client provisioning &amp; self-service) and the
            <strong> S3 access</strong> endpoints, and <strong>owns</strong> the clients &amp; bypass rules it creates.
            Shown once at creation; stored only as a hash.
          </p>
        </div>
        <button className="btn btn-primary" onClick={() => setAddOpen(true)}>
          <I.Plus size={13}/> New token
        </button>
      </div>

      {err && <div className="card"><div className="field-error" style={{ padding: 12 }}>Failed to load: {err}</div></div>}

      {!loading && !err && tokens.length === 0 && (
        <div className="card">
          <Empty icon={<I.Key size={28}/>} title="No access tokens"
                 message="Issue a token so an external app can call the Shoptefy API."/>
        </div>
      )}

      {/* Active tokens */}
      {active.length > 0 && (
        <div className="card">
          <table className="tbl">
            <thead>{TOKEN_COLS}</thead>
            <tbody>{active.map((t) => <TokenRow key={t.id} t={t} onRevoke={setRevokeTarget}/>)}</tbody>
          </table>
        </div>
      )}
      {!loading && !err && tokens.length > 0 && active.length === 0 && (
        <div className="card">
          <Empty icon={<I.Key size={26}/>} title="No active tokens"
                 message="Every token is revoked or expired. Issue a new one above."/>
        </div>
      )}

      {/* Revoked & expired — kept separate, auto-removed after a retention window */}
      {dead.length > 0 && (
        <>
          <div className="spread" style={{ alignItems: 'baseline', margin: '20px 2px 8px' }}>
            <h2 style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-3)', textTransform: 'uppercase', letterSpacing: '.06em', margin: 0 }}>
              Revoked &amp; expired <span className="muted" style={{ fontWeight: 400 }}>· {dead.length}</span>
            </h2>
            <span className="muted" style={{ fontSize: 11.5 }}>Automatically removed 30 days after they die</span>
          </div>
          <div className="card">
            <table className="tbl">
              <thead>{TOKEN_COLS}</thead>
              <tbody>{dead.map((t) => <TokenRow key={t.id} t={t}/>)}</tbody>
            </table>
          </div>
        </>
      )}

      <CreateAccessTokenModal open={addOpen}
                              onClose={() => setAddOpen(false)}
                              onCreated={async (resp) => { setAddOpen(false); setNewPlain(resp); await reload(); }}/>

      <Modal open={newPlain !== null}
             onClose={() => setNewPlain(null)}
             title="Token created — copy it now"
             subtitle="This is the only time the plaintext is shown. The server only stores its SHA-256 hash."
             footer={<button className="btn btn-primary" onClick={() => setNewPlain(null)}>I have saved it</button>}>
        {newPlain && (
          <div className="col" style={{ gap: 10 }}>
            <div className="muted" style={{ fontSize: 12.5 }}>Label: <span className="mono">{newPlain.label}</span></div>
            <div className="mono" style={{ padding: 12, background: 'var(--bg-3)', borderRadius: 6, fontSize: 12, wordBreak: 'break-all', userSelect: 'all' }}>
              {newPlain.token}
            </div>
            <button className="btn btn-sm" onClick={() => navigator.clipboard?.writeText(newPlain.token).then(() => toast?.success('Copied'))}>
              <I.Copy size={12}/> Copy to clipboard
            </button>
          </div>
        )}
      </Modal>

      <Modal open={revokeTarget !== null}
             onClose={() => { if (!revoking) setRevokeTarget(null); }}
             title="Revoke token?"
             subtitle="Any app using it stops authenticating immediately. This can't be undone."
             footer={
               <>
                 <button className="btn" disabled={revoking} onClick={() => setRevokeTarget(null)}>Cancel</button>
                 <button className="btn btn-danger-solid" disabled={revoking} onClick={doRevoke}>
                   <I.Trash size={13}/> {revoking ? 'Revoking…' : 'Revoke'}
                 </button>
               </>
             }>
        {revokeTarget && (
          <div className="mono" style={{ fontSize: 12, padding: 10, background: 'var(--bg-2)', borderRadius: 6 }}>
            {revokeTarget.label}
          </div>
        )}
      </Modal>
    </div>
  );
}

// ─── Create modal ────────────────────────────────────────────────────────────

const EXPIRY_OPTIONS: { label: string; value?: string }[] = [
  { label: 'Never', value: undefined },
  { label: '30 days', value: '720h' },
  { label: '90 days', value: '2160h' },
  { label: '1 year', value: '8760h' },
];

function CreateAccessTokenModal({
  open, onClose, onCreated,
}: {
  open: boolean;
  onClose: () => void;
  onCreated: (resp: CreateAccessTokenResponse) => Promise<void> | void;
}) {
  const toast = useToast();
  const [label, setLabel] = useState('');
  const [expiryIdx, setExpiryIdx] = useState(2); // default 90 days
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => { if (open) { setLabel(''); setExpiryIdx(2); setSubmitting(false); } }, [open]);

  const canSubmit = label.trim().length > 0;

  const submit = async () => {
    if (!canSubmit) return;
    setSubmitting(true);
    try {
      const resp = await createAccessToken(label.trim(), EXPIRY_OPTIONS[expiryIdx].value);
      await onCreated(resp);
    } catch (e) {
      toast?.error('Create failed', e instanceof ApiError ? e.message : String(e));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal open={open} onClose={onClose}
           title="New access token"
           subtitle="A machine token for an external app to call the Shoptefy API."
           footer={
             <>
               <button className="btn" onClick={onClose}>Cancel</button>
               <button className="btn btn-primary" disabled={submitting || !canSubmit} onClick={submit}>
                 {submitting ? 'Creating…' : 'Create'}
               </button>
             </>
           }>
      <div className="col" style={{ gap: 12 }}>
        <div className="field">
          <label className="field-label">Label <span className="req-dot">*</span></label>
          <input className="input" placeholder="e.g. billing-sync, partner-api"
                 value={label} onChange={(e) => setLabel(e.target.value)} autoFocus
                 onKeyDown={(e) => { if (e.key === 'Enter' && canSubmit) submit(); }}/>
          <div className="field-hint">A human name so you can tell which app a token belongs to.</div>
        </div>
        <div className="field">
          <label className="field-label">Expires</label>
          <select className="select" value={expiryIdx} onChange={(e) => setExpiryIdx(Number(e.target.value))}>
            {EXPIRY_OPTIONS.map((o, i) => <option key={i} value={i}>{o.label}</option>)}
          </select>
          <div className="field-hint">You can revoke a token any time regardless of expiry.</div>
        </div>
      </div>
    </Modal>
  );
}
